import React from 'react';
import sha1 from 'js-sha1';
import { ALL_METRICS_OPTIONS, getCohortFieldAndPeriod } from './metricsUtils';
import { SERVER_DEFAULT_ICON_URL } from '../utils/apps';
import css from './page.css';
import { FormattedCreativeTypes } from './types';
import { Dimensions } from './consts';

const SHA_PREFIX = '1290726c5000cab87381bcd9f7b3f39a95586c32';
const CDN_URL_PREFIX = 'https://cdn.singular.net/dashboard/creatives/';
export const UNKNOWN_INDICATION = 'N/A';
export const DEFAULT_ICON_SRC = '/static/dashboard_react/src/resources/icons/default_app_icon.svg';

export const formatValue = value => {
    if (value === null || value === undefined || value === '') return UNKNOWN_INDICATION;

    if (Array.isArray(value)) return !value.length ? UNKNOWN_INDICATION : value.join(', ');

    return String(value);
};

export const getCreativeDisplayName = multiValuedDimensions => {
    const { assetNameList, creativeNameList } = multiValuedDimensions || {};
    const namesList = assetNameList?.length ? assetNameList : creativeNameList || [];
    return formatValue(namesList);
};

export const mapFieldsToMultiValuedStructure = fields =>
    fields.map(field => ({ originalField: field, aggregatedField: field }));

export const MULTI_VALUED_FIELDS = [
    { originalField: Dimensions.OS, aggregatedField: 'osList' },
    { originalField: Dimensions.ASSET_NAME, aggregatedField: 'assetNameList' },
    { originalField: Dimensions.CREATIVE_NAME, aggregatedField: 'creativeNameList' },
    { originalField: Dimensions.ASSET_ID, aggregatedField: 'assetIdList' },
    { originalField: Dimensions.CREATIVE_ID, aggregatedField: 'creativeIdList' },
    { originalField: Dimensions.CREATIVE_VIDEO_DURATION, aggregatedField: 'creativeVideoDurationList' },
    { originalField: Dimensions.CAMPAIGN_NAME, aggregatedField: 'campaignNameList', showMultiValuedTag: true },
    {
        originalField: Dimensions.COUNTRY,
        aggregatedField: 'countryList',
        group: 'metadata_dimensions',
        showMultiValuedTag: true,
        isLazy: true,
    },
    {
        originalField: Dimensions.ACCOUNT_NAME,
        aggregatedField: 'accountNameList',
        group: 'metadata_dimensions',
        showMultiValuedTag: true,
        isLazy: true,
    },
];

export const getMultiValuedFieldsByGroup = (activeLazyFields = []) => {
    const activeLazySet = new Set(activeLazyFields);
    return MULTI_VALUED_FIELDS.filter(
        ({ originalField, isLazy }) => !isLazy || activeLazySet.has(originalField)
    ).reduce((acc, { originalField, group }) => {
        const dimensionsGroup = group || 'dimensions';
        acc[dimensionsGroup] = acc[dimensionsGroup] || [];
        acc[dimensionsGroup].push(originalField);
        return acc;
    }, {});
};

const getItemIcon = url => {
    const src = !url || url === SERVER_DEFAULT_ICON_URL ? DEFAULT_ICON_SRC : url;
    return <img className={css.autocompleteOptionIcon} src={src} alt={src} />;
};

export const convertObjectToOption = (item, iconField) => ({
    value: item.name,
    label: item.displayName,
    icon: getItemIcon(item[iconField]),
});

export const getOptionsList = (items, iconField) => items.map(item => convertObjectToOption(item, iconField));

export const convertHashToCdnUrl = creativeHash => {
    const hash = sha1(`${SHA_PREFIX}${creativeHash}`);
    return `${CDN_URL_PREFIX}${hash}`;
};

const excludeUnknownsValues = (aggregatedDimensions, creativeImageHash, fieldName) => {
    aggregatedDimensions[creativeImageHash][fieldName] = [...aggregatedDimensions[creativeImageHash][fieldName]].filter(
        value => value !== UNKNOWN_INDICATION
    );
};

// Grouping the multivalued dimensions by creative
export const getAggregatedDimensionsByCreative = (multiValuedDimensions, multiValuedFields = MULTI_VALUED_FIELDS) => {
    const aggregatedDimensions = (multiValuedDimensions || []).reduce((acc, creative) => {
        const creativeAggregatedDimensions = acc[creative.creative_image_hash] || {};

        multiValuedFields.forEach(({ aggregatedField, originalField }) => {
            const creativeAggregatedDimensionField = creativeAggregatedDimensions[aggregatedField] || new Set();
            // A lazy dimension that hasn't been requested yet is absent from the response; keep its
            // aggregated list empty instead of polluting it with an `undefined` entry.
            if (creative[originalField] !== undefined) creativeAggregatedDimensionField.add(creative[originalField]);
            creativeAggregatedDimensions[aggregatedField] = creativeAggregatedDimensionField;
        });

        acc[creative.creative_image_hash] = creativeAggregatedDimensions;

        return acc;
    }, {});

    Object.keys(aggregatedDimensions).forEach(creativeImageHash => {
        multiValuedFields.forEach(({ aggregatedField }) => {
            excludeUnknownsValues(aggregatedDimensions, creativeImageHash, aggregatedField);
        });
    });

    return aggregatedDimensions;
};

const getCreativeFieldValue = (field, creative, isCohort) => {
    if (!isCohort) return creative[field];
    const [cohortField, cohortPeriod] = getCohortFieldAndPeriod(field);
    return cohortPeriod ? creative[cohortField]?.[cohortPeriod] || creative[field] : creative[cohortField];
};

const createCreativeFieldsObject = (fields, creative, isCohort = false) => {
    return fields.reduce((acc, { value }) => {
        acc[value] = getCreativeFieldValue(value, creative, isCohort) || 0;
        return acc;
    }, {});
};

export const hasValidMetrics = (
    metrics,
    cohorts,
    selectedMetrics,
    selectedCohorts,
    pendingMetrics = [],
    pendingCohorts = []
) => {
    const pending = new Set([...pendingMetrics, ...pendingCohorts]);
    return (
        selectedMetrics.some(({ value }) => !!metrics[value] || pending.has(value)) ||
        selectedCohorts.some(({ value }) => !!cohorts[value] || pending.has(value))
    );
};

const omitKeys = (fields, keys) => {
    const next = { ...fields };
    keys.forEach(key => delete next[key]);
    return next;
};

// Drop the given metric/cohort keys from each creative so the card renders a loading skeleton
// (an undefined value) for them instead of a stale 0 / N/A while they are still being fetched.
export const blankPendingFields = (creatives, pendingMetrics = [], pendingCohorts = []) => {
    if (!pendingMetrics.length && !pendingCohorts.length) return creatives;
    return creatives.map(creative => ({
        ...creative,
        ...(pendingMetrics.length && { metrics: omitKeys(creative.metrics, pendingMetrics) }),
        ...(pendingCohorts.length && { cohorts: omitKeys(creative.cohorts, pendingCohorts) }),
    }));
};

export const formatCreatives = (
    creatives,
    selectedMetrics,
    selectedCohorts,
    creativesMultiValuedDimensions,
    pendingMetrics = [],
    pendingCohorts = []
) => {
    if (!creatives?.length) return [];

    const aggregatedDimensionsByCreative = getAggregatedDimensionsByCreative(creativesMultiValuedDimensions);

    return creatives.reduce((acc, creative) => {
        const metrics = createCreativeFieldsObject(ALL_METRICS_OPTIONS, creative);
        const cohorts = createCreativeFieldsObject(selectedCohorts, creative, true);

        if (hasValidMetrics(metrics, cohorts, selectedMetrics, selectedCohorts, pendingMetrics, pendingCohorts)) {
            const imageHash = creative.creative_image_hash;
            acc.push({
                imageHash,
                type: FormattedCreativeTypes[creative.creative_type],
                embeddingUrl: creative.creative_embedding_url,
                height: parseInt(creative.creative_height, 10),
                width: parseInt(creative.creative_width, 10),
                url: convertHashToCdnUrl(imageHash),
                orientation: creative.creative_orientation,
                videoDurationRange: creative.creative_video_duration_range,
                multiValuedDimensions: aggregatedDimensionsByCreative[imageHash] ?? {},
                metrics,
                cohorts,
            });
        }

        return acc;
    }, []);
};
